import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { distinctUntilChanged, takeUntil, switchMap, map } from 'rxjs/operators';

import { DOCUMENT } from '@angular/common';
import { Injectable, Inject, OnDestroy } from '@angular/core';

import { Theme } from '../enums';
import { DEFAULT_THEME, THEME_CONFIG } from '../providers';
import { ThemeConfig } from '../types';

@Injectable()
export class ThemeService implements OnDestroy {
  private readonly _themeLinks: HTMLLinkElement[] = [];

  private readonly _theme$: BehaviorSubject<Theme>;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(THEME_CONFIG) private readonly _config: ThemeConfig,
    @Inject(DEFAULT_THEME) private readonly _defaultTheme: Theme,
    @Inject(DOCUMENT) private readonly _document: Document,
  ) {
    this._theme$ = new BehaviorSubject(this._defaultTheme);

    this._theme$
      .pipe(
        distinctUntilChanged(),
        switchMap(this._loadTheme.bind(this)),
        takeUntil(this._destroy$),
      )
      .subscribe((element: HTMLLinkElement) => {
        this._themeLinks.push(element);

        if (this._themeLinks.length === 2) {
          this._document.head.removeChild(this._themeLinks.shift());
        }
      });
  }

  get isDark$(): Observable<boolean> {
    return this._theme$.pipe(map((theme) => theme === Theme.Dark));
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public setTheme(theme: Theme): void {
    this._theme$.next(theme);
  }

  public resetTheme(): void {
    this._theme$.next(this._defaultTheme);
  }

  private _loadTheme(name: Theme): Observable<HTMLLinkElement> {
    return new Observable((observer) => {
      const link = this._document.createElement('link');
      link.setAttribute('rel', 'stylesheet');
      link.setAttribute('type', 'text/css');
      link.setAttribute('href', this._config[name]);

      link.onload = () => {
        observer.next(link);
        observer.complete();
      };

      this._document.head.appendChild(link);
    });
  }
}
